// Centralized Lumotis homepage content.
import {
  Brain,
  Search,
  MapPin,
  Megaphone,
  Users,
  FileText,
  Mail,
  Zap,
  Monitor,
  TrendingUp,
  Play,
  Star,
  ShoppingBag,
  Home,
  Scissors,
  Globe,
  Layers,
  ShieldCheck,
  Sparkles,
  HeartHandshake,
  Activity,
  BarChart3,
  UtensilsCrossed,
  GraduationCap,
  Briefcase,
  Leaf,
  Plane,
  Compass,
  Wrench,
} from "lucide-react";

export const NAV_LINKS = [
  { label: "Services", href: "#services" },
  { label: "Why Us", href: "#why" },
  { label: "Process", href: "#process" },
  { label: "Case Studies", href: "#case-studies" },
  { label: "Pricing", href: "#pricing" },
  { label: "Blog", href: "#blog" },
];

export const TRUST_BADGES = [
  { value: "200+", label: "Clients Served" },
  { value: "$4M+", label: "Revenue Generated" },
  { value: "15+", label: "Industries" },
  { value: "4.9/5", label: "Client Rating" },
];

export const CLIENT_LOGOS = [
  "Aurora",
  "Nimbly",
  "Halo Health",
  "FinKart",
  "Skydeck",
  "EduPond",
  "Mealfox",
  "Voyage",
  "TideMusic",
  "Kindred",
  "BrightSpace",
  "Apex Realty",
];

export const SERVICES = [
  {
    icon: Brain,
    title: "AI Search Optimization (GEO/AEO)",
    short: "AI Search",
    slug: "ai-search-optimization",
    description:
      "Get found when customers ask AI. We make your brand show up in ChatGPT, Gemini, and Google AI Overviews — the new way people search. Includes AI visibility audit, content structuring for answer engines, citation building, and monthly AI-mention tracking.",
    bullets: ["AI visibility audit", "Citation building", "Monthly AI tracking"],
    span: "lg:col-span-2",
    accent: true,
  },
  {
    icon: Search,
    title: "Search Engine Optimization (SEO)",
    short: "SEO",
    slug: "seo",
    description:
      "Rank higher, get found first. Technical SEO, on-page optimization, keyword strategy, and link building to grow organic traffic and leads.",
    bullets: ["Technical SEO", "Keyword strategy", "Link building"],
  },
  {
    icon: MapPin,
    title: "Local SEO",
    short: "Local",
    slug: "local-seo",
    description:
      "Dominate 'near me' searches. Google Business Profile optimization, local citations, map-pack ranking, and review management for businesses serving a specific area.",
    bullets: ["GBP optimization", "Map-pack ranking", "Review management"],
  },
  {
    icon: Megaphone,
    title: "Paid Advertising (PPC)",
    short: "PPC",
    slug: "paid-advertising",
    description:
      "Turn ad spend into measurable sales. Meta and Google Ads management — campaign setup, targeting, A/B testing, and ROI-focused optimization.",
    bullets: ["Google Ads", "Meta Ads", "A/B testing"],
  },
  {
    icon: Users,
    title: "Social Media Management",
    short: "Social",
    slug: "social-media",
    description:
      "Stay top-of-mind, grow your audience. Content calendar, post design, scheduling, community engagement, and monthly performance reporting across platforms.",
    bullets: ["Content calendar", "Community management", "Monthly reporting"],
    span: "lg:col-span-2",
    accent: true,
  },
  {
    icon: Mail,
    title: "Email & SMS Marketing",
    short: "Email",
    slug: "email-sms",
    description:
      "Turn your list into recurring revenue. Automated flows, newsletters, promotional campaigns, and segmentation to nurture leads and drive repeat sales.",
    bullets: ["Automated flows", "Newsletters", "Segmentation"],
  },
];

export const INDUSTRY_SOLUTIONS = [
  {
    icon: ShoppingBag,
    title: "Shopify & Ecommerce Marketing",
    short: "Ecommerce",
    slug: "shopify-ecommerce",
    description:
      "Scale your store's revenue. Meta + Google Shopping ads, Klaviyo email & SMS flows, product-page CRO, and AI shopping visibility. Built around ROAS and revenue.",
    bullets: ["Shopping ads", "Klaviyo flows", "Product-page CRO"],
  },
  {
    icon: Home,
    title: "Real Estate Marketing",
    short: "Real Estate",
    slug: "real-estate-marketing",
    description:
      "A steady pipeline of qualified buyers and sellers. Lead-generation ads, neighborhood landing pages, automated lead follow-up, and local SEO to capture and convert more leads.",
    bullets: ["Lead-gen ads", "Local SEO", "Automated follow-up"],
  },
  {
    icon: Scissors,
    title: "Salon & Spa Marketing",
    short: "Salon & Spa",
    slug: "salon-spa-marketing",
    description:
      "Fill every chair, every day. Instagram management, local ads with before/after content, Google Business optimization, online booking integration, and review automation.",
    bullets: ["Instagram growth", "Local ads", "Booking integration"],
  },
];

export const LOGO_URL = null;

export const FOOTER_LOGO_URL = null;

export const WHY_US = [
  {
    icon: Brain,
    title: "AI-first marketing",
    text: "We optimize for ChatGPT, Gemini and Google AI Overviews — not just traditional search. Your brand shows up wherever your customers are asking questions.",
  },
  {
    icon: Layers,
    title: "Full-stack under one roof",
    text: "SEO, paid ads, social, email, automation and web — all coordinated. No agency handoffs, no finger-pointing, no gaps.",
  },
  {
    icon: Globe,
    title: "15+ industries covered",
    text: "Ecommerce, real estate, salons, healthcare, finance, restaurants — we've built playbooks from real results, not theory.",
  },
  {
    icon: ShieldCheck,
    title: "Transparent ROI reporting",
    text: "Live dashboards, weekly performance memos, and monthly strategy calls. You always know exactly what your marketing dollars are doing.",
  },
  {
    icon: HeartHandshake,
    title: "Dedicated growth managers",
    text: "You get a senior strategist who owns your results — not a junior account exec reading from a template.",
  },
  {
    icon: Sparkles,
    title: "Industry-proven playbooks",
    text: "Every engagement starts with a playbook refined across hundreds of client campaigns in your specific industry.",
  },
];

export const APP_CATEGORIES = [
  { icon: ShoppingBag, label: "Ecommerce" },
  { icon: Home, label: "Real Estate" },
  { icon: Scissors, label: "Salon & Spa" },
  { icon: Activity, label: "Healthcare" },
  { icon: BarChart3, label: "Finance" },
  { icon: UtensilsCrossed, label: "Restaurants" },
  { icon: GraduationCap, label: "Education" },
  { icon: Briefcase, label: "Professional Svcs" },
  { icon: Star, label: "Law Firms" },
  { icon: Leaf, label: "Wellness" },
  { icon: TrendingUp, label: "Fitness" },
  { icon: Plane, label: "Travel" },
];

export const PROCESS_STEPS = [
  {
    icon: Search,
    title: "Audit",
    text: "Deep dive into your current digital presence — SEO health, ad performance, social reach, content gaps, and AI visibility.",
  },
  {
    icon: Compass,
    title: "Strategy",
    text: "Custom growth roadmap mapped to your industry, target audience, and revenue goals — with clear KPIs for every channel.",
  },
  {
    icon: Wrench,
    title: "Execute",
    text: "Content, ads, SEO, automation, and social — all built and launched with precision, on time and on brief.",
  },
  {
    icon: Activity,
    title: "Measure",
    text: "Weekly reporting on traffic, leads, ROAS, and AI-mention share. No vanity metrics — only numbers that move your business.",
  },
  {
    icon: TrendingUp,
    title: "Scale",
    text: "Double down on winning channels, expand to new audiences, and add channels as revenue grows.",
  },
];

export const STATS = [
  { value: 200, suffix: "+", label: "Clients grown" },
  { value: 4.2, suffix: "M+", label: "Revenue generated ($)", decimals: 1 },
  { value: 15, suffix: "+", label: "Industries served" },
  { value: 4.9, suffix: "/5", label: "Avg. client rating", decimals: 1 },
  { value: 98, suffix: "%", label: "Client satisfaction" },
];

export const CASE_STUDIES = [
  {
    category: "Ecommerce",
    title: "340% ROAS uplift for a Shopify fashion brand",
    metric: "+340%",
    metricLabel: "return on ad spend in 90 days",
    summary:
      "Rebuilt Meta + Google Shopping campaigns, launched Klaviyo email flows, and optimized product pages — turning a break-even ad account into the brand's #1 revenue channel.",
    market: "USA • Shopify",
  },
  {
    category: "Real Estate",
    title: "3× qualified leads for a regional real estate agency",
    metric: "3×",
    metricLabel: "more qualified leads in 60 days",
    summary:
      "Local SEO, Google Ads targeting by neighborhood, and automated lead follow-up sequences filled the pipeline with buyers and sellers ready to act.",
    market: "Texas, USA • Local SEO + PPC",
  },
  {
    category: "Salon & Spa",
    title: "60% more bookings across a 3-location salon chain",
    metric: "+60%",
    metricLabel: "increase in bookings in 45 days",
    summary:
      "Instagram growth with before/after content, Google Business optimization, and review automation drove consistent new-client flow and cut no-show rates.",
    market: "UK • Social + Local SEO",
  },
];

export const TESTIMONIALS = [
  {
    name: "Sarah Chen",
    role: "Founder, Aurora Studio (Shopify)",
    text: "Lumotis turned our dead Facebook Ads account into our #1 revenue channel. ROAS went from 1.2× to 4.8× in three months. Genuinely transformative.",
    rating: 5,
    avatar:
      "https://images.unsplash.com/photo-1762522926157-bcc04bf0b10a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NzYyNTQzNHww&ixlib=rb-4.1.0&q=85",
  },
  {
    name: "Marcus Blake",
    role: "Principal Agent, Apex Realty (Texas)",
    text: "We were invisible online. In 4 months Lumotis had us showing up #1 for every neighborhood search that matters — our pipeline is full for the first time in years.",
    rating: 5,
    avatar:
      "https://images.unsplash.com/photo-1769636929388-99eff95d3bf1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NzYyNTQzNHww&ixlib=rb-4.1.0&q=85",
  },
  {
    name: "Priya Sharma",
    role: "Owner, Kindred Salon (London)",
    text: "Our Instagram grew from 800 to 12,000 followers and the booking calendar filled for 3 weeks solid. Lumotis understood our brand from day one.",
    rating: 5,
    avatar:
      "https://images.pexels.com/photos/32721690/pexels-photo-32721690.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

export const PRICING = [
  {
    tier: "Starter",
    price: "$997",
    cadence: "/month",
    description:
      "For local businesses ready to get serious about digital marketing.",
    features: [
      "Local SEO (GBP + citations)",
      "Social media management (2 platforms)",
      "Monthly performance report",
      "Email support",
      "1 strategy call per month",
    ],
    cta: "Start with Starter",
    highlighted: false,
  },
  {
    tier: "Growth",
    price: "$2,997",
    cadence: "/month",
    description:
      "For scaling brands that want the full digital marketing stack working together.",
    features: [
      "SEO + AI Search Optimization (GEO/AEO)",
      "Paid Ads (Google + Meta)",
      "Social media management (4 platforms)",
      "Email & SMS marketing",
      "Weekly reporting + Slack access",
      "Dedicated growth manager",
    ],
    cta: "Get the Growth plan",
    highlighted: true,
  },
  {
    tier: "Enterprise",
    price: "Custom",
    cadence: "",
    description:
      "For multi-location businesses and ecommerce brands scaling aggressively.",
    features: [
      "Full channel mix (all services)",
      "Multi-location or multi-brand management",
      "Custom reporting dashboard",
      "Content production (video + blog)",
      "CRO & Marketing Automation",
      "Priority 24/7 support",
    ],
    cta: "Talk to sales",
    highlighted: false,
  },
];

export const BLOG_POSTS = [
  {
    category: "AI Search",
    title: "AI Search Optimization in 2026: how to get your brand cited by ChatGPT and Gemini",
    excerpt:
      "GEO/AEO is the new SEO. Here's exactly how to structure your content so answer engines recommend your business.",
    readTime: "9 min read",
    cover:
      "https://images.unsplash.com/photo-1770013277247-ab7a08d3d9ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGNvbXBhbnklMjBsb2dvfGVufDB8fHx8MTc3NzYyOTgwOHww&ixlib=rb-4.1.0&q=85",
  },
  {
    category: "Local SEO",
    title: "Local SEO for small businesses: the complete 2026 guide",
    excerpt:
      "Map-pack rankings, Google Business Profile, review management, and local citations — everything that drives 'near me' traffic.",
    readTime: "11 min read",
    cover:
      "https://images.unsplash.com/photo-1770210217380-d78a69acdc77?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGNvbXBhbnklMjBsb2dvfGVufDB8fHx8MTc3NzYyOTgwOHww&ixlib=rb-4.1.0&q=85",
  },
  {
    category: "Paid Ads",
    title: "Meta Ads in 2026: how to maximize ROAS with systematic creative testing",
    excerpt:
      "The creative testing framework we use to take Shopify and ecommerce brands from break-even to 4×+ ROAS.",
    readTime: "8 min read",
    cover:
      "https://images.pexels.com/photos/13597064/pexels-photo-13597064.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

export const MARKETS = [
  "USA",
  "United Kingdom",
  "Australia",
  "UAE",
  "Canada",
  "Germany",
  "Japan",
  "Singapore",
  "Brazil",
  "India",
];

export const HERO_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/1de62c85-d46e-42c6-a01d-bc940b22809d/images/e9584472188d7eb7c84c1053e2153017c49efbe341ff5d1aa8fb942fbdb5ef12.png";

export const GROWTH_ABSTRACT =
  "https://static.prod-images.emergentagent.com/jobs/1de62c85-d46e-42c6-a01d-bc940b22809d/images/3578074adbee8e5cb08627e35dd55206ea0b874e8ba66c6ff73e0b4787330848.png";
